import { atomFamily } from 'recoil';
import type { Layout } from 'react-grid-layout';
import type { BlockId } from '../studio';
import { resetOnStudioReset } from '../effects/signalEffects';

const CONTINUATION_LAYOUT_ID_PREFIX = '__cont__';
const CONTINUATION_LAYOUT_ID_SEPARATOR = '::';

export interface ContinuationMeta {
  readonly sourceBlockId: BlockId;
  readonly continuationIndex: number;
  readonly sourcePageIndex: number;
}

export const makeContinuationLayoutId = ({
  sourceBlockId,
  continuationIndex,
}: {
  sourceBlockId: BlockId;
  continuationIndex: number;
}): string =>
  `${CONTINUATION_LAYOUT_ID_PREFIX}${CONTINUATION_LAYOUT_ID_SEPARATOR}${sourceBlockId}${CONTINUATION_LAYOUT_ID_SEPARATOR}${continuationIndex}`;

export const isContinuationLayoutId = (id: string): boolean => id.startsWith(CONTINUATION_LAYOUT_ID_PREFIX);

// Defined here rather than in renderedPages.ts so blockExpansion.ts can read it without a circular import.
export const continuationUserBlocksState = atomFamily<Layout, string>({
  key: 'continuationUserBlocks',
  default: [],
  effects: [resetOnStudioReset],
});

export const parseContinuationLayoutId = (
  id: string,
): { sourceBlockId: BlockId; continuationIndex: number } | undefined => {
  if (!isContinuationLayoutId(id)) return undefined;
  const parts = id.split(CONTINUATION_LAYOUT_ID_SEPARATOR);
  if (parts.length !== 3) return undefined;
  const continuationIndex = Number(parts[2]);
  if (!Number.isInteger(continuationIndex) || continuationIndex < 0) return undefined;
  return { sourceBlockId: parts[1]!, continuationIndex };
};
